<template>
  <b-row
    class="type-of-support-box py-2 py-md-4 rounded"
    :class="disabled ? '' : 'border-primary'"
    :align-h="align"
    ref="top"
  >
    <b-col cols="12">
      <b-row no-gutters>
        <b-col v-if="label" cols="auto" v-orphans>
          <label
            @click="onClick(option)"
            class="type-of-support-box__label mb-2 font-weight-bold text-blue-500 tosb-container d-flex align-items-baseline pl-2 ml-1"
          >
            <input type="radio" name="typeOfSupport" :value="option" :checked="value === option" />
            <span
              v-if="showRadioBox"
              class="type-of-support-box__checkmark checkmark border-blue-300"
            ></span>
            <span>{{ label }}</span>
          </label>
        </b-col>
        <b-col v-if="description" cols="12" v-orphans>
          <p
            class="type-of-support-box__description text-blue-500 d-inline-flex no-gutters pl-2"
            :class="[{ 'pl-md-4 ml-4 md-2': showRadioBox }]"
          >
            <b-icon-info-circle
              v-if="showNotificationIcon"
              variant="blue-500"
              class="mr-2 mt-1"
            ></b-icon-info-circle>
            <span class="type-of-support-box__text">{{ description }}</span>
          </p>
        </b-col>

        <b-col
          v-if="options.length > 0"
          cols="12"
          class="mt-2 d-flex flex-wrap justify-content-between"
          :class="[{ 'd-flex': !equalOptions }]"
          role="radiogroup"
        >
          <TypeOfSupportBoxOption
            v-for="(box, index) in options"
            :key="index"
            v-bind="box"
            :amount="amount"
            @amount="$emit('amount', $event)"
          ></TypeOfSupportBoxOption>
        </b-col>

        <b-col v-if="amount < 0" cols="12" md="6" class="px-0 px-md-2">
          <FormInput
            :rules="amountValidationRules"
            v-mask="'######'"
            autofocus
            :placeholder="$t('form.label.provideOtherAmount')"
            name="otherAmount"
            :clear-on-destroy="true"
          >
            <b-input-group-append>
              <b-input-group-prepend is-text tag="span">
                {{ $t('form.label.pln') }}
              </b-input-group-prepend>
            </b-input-group-append>
          </FormInput>
          <div v-if="!getValidationState" class="mt-1 ml-1 d-flex align-items-center">
            <b-icon-info-circle variant="blue-500" class="mr-2 mt-1"></b-icon-info-circle>
            <span class="fs-08">{{ $t('form.typeOfSupport.defaultValueHint') }}</span>
          </div>
        </b-col>
        <b-col v-if="isDonation && showSurplusInfo" cols="12" md="6">
          <p class="mx-4 mb-2 mb-md-0 text-base text-blue-500 d-inline-block">
            <span
              class="type-of-support-box__clock-icon noun-clock-green mr-1 d-inline-flex position-relative"
            ></span>
            {{ $t('form.typeOfSupport.surplusInfo') }}
          </p>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { useFieldError, useFieldValue } from 'vee-validate'
import { computed } from 'vue'

import { SOFT_PAYMENT_LIMIT } from '@/constants/payment'
import { PaymentType } from '@/types'

import FormInput from '@/components/widgets/forms/FormInput.vue'
import { useButtonAlign } from '@/composables'
import { convertToMajorUnit } from '@/helpers/money'
import TypeOfSupportBoxOption from './TypeOfSupportBoxOption.vue'

export default {
  name: 'TypeOfSupportBox',
  components: { FormInput, TypeOfSupportBoxOption },
  props: {
    value: {},
    label: {},
    option: {},
    options: {
      type: Array,
      default: () => []
    },
    showRadioBox: {
      required: false,
      default: true,
      type: Boolean
    },
    description: {},
    equalOptions: {
      default: false,
      type: Boolean
    },
    scrollEnabled: {
      default: true,
      type: Boolean
    },
    useOrphansDirective: {
      default: true,
      type: Boolean
    }
  },
  computed: {
    disabled() {
      return this.value !== this.option
    },
    isDonation() {
      return this.option === PaymentType.fundraisingDonate
    },
    defaultValue() {
      const value = this.isFundraising ? this.options[1].value : null
      return this.options.length > 0 ? value : SOFT_PAYMENT_LIMIT
    },
    addValueText() {
      if (this.option === PaymentType.instalments) return this.$t('form.label.installmentMobile')
      return this.isFundraising ? this.$t('form.label.add') : this.$t('form.label.value')
    },
    showNotificationIcon() {
      return [PaymentType.instalments, PaymentType.fundraising].includes(this.option)
    },
    showSurplusInfo() {
      return this.amount < 0 && this.formValue >= this.remaining
    },
    remaining() {
      return this.$store.getters['child/getPaidAmount']
        ? SOFT_PAYMENT_LIMIT - convertToMajorUnit(this.$store.getters['child/getPaidAmount'])
        : SOFT_PAYMENT_LIMIT
    }
  },
  watch: {
    '$attrs.amount': {
      handler: function (amount) {
        this.amount = amount
      },
      immediate: true
    }
  },
  methods: {
    onClick(option) {
      this.$emit('input', option)
      if (this.scrollEnabled && this.$refs?.top) {
        this.$refs.top.scrollIntoView(this.scrollArgs)
      }
    }
  },
  data() {
    return {
      amount: null,
      PaymentType,
      scrollArgs: { behavior: 'smooth', block: 'start', inline: 'nearest' }
    }
  },
  created() {
    if (this.amount == null) {
      this.amount = this.defaultValue
    }
  },
  setup(props) {
    const isFundraising = computed(() => props.option === PaymentType.fundraising)
    const { align } = useButtonAlign()

    const formValue = useFieldValue('otherAmount')
    const errors = useFieldError('otherAmount')

    const getValidationState = computed(() => (errors.value ? false : null))

    const amountValidationRules = computed(
      () => `required|${isFundraising.value ? 'min_fundraising:100|max_value:999' : 'min_donate:1'}`
    )
    return {
      formValue,
      isFundraising,
      errors,
      getValidationState,
      align,
      amountValidationRules
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/scss/app';

.type-of-support-box {
  &__label {
    font-size: 1.2rem;

    @include media-breakpoint-up(md) {
      font-size: 1.5rem;
    }
  }

  &__description {
    font-size: 0.8rem;

    @include media-breakpoint-up(md) {
      font-size: 1rem;
    }
  }

  &__checkmark {
    flex: 0 0 auto;
    top: 3px;
  }

  &__text {
    flex: 1;
  }

  &__clock-icon {
    top: 5px;
    display: inline-flex;
  }
}
</style>
